// Copyright (c) 2020-2021 Visual Intelligence LP. All rights reserved.
//
import * as React from 'react';
import { withStyles } from "tss-react/mui";
import { Switch as MuiSwitch } from '@mui/material';
import { SwitchProps as MuiSwitchProps } from '@mui/material';
import { Field } from 'mobx-react-form';
import { observer } from 'mobx-react';

import Ellipsis from '@shared/components/Ellipsis';
import { getScrollAttr } from '@shared/utils/form';
import { getTestAttr } from '@shared/utils/common';

export interface SwitchProps extends Omit<MuiSwitchProps, 'classes' | 'onChange'> {
  className?: string;
  testAttributeArgs: {
    module: string;
    elementDescription: string;
  };
  field?: Field;
  errorText?: string;
  error?: boolean;
  onChange?: (checked: boolean, e) => any;
}

@observer
class Switch extends React.Component<SwitchProps> {
  private handleChange = (e, checked: boolean) => {
    const { field, onChange } = this.props;

    if (field) {
      field.set('value', checked);
    }

    if (onChange) {
      onChange(checked, e);
    }
  }

  render() {
    const { field, errorText, error, checked, onChange, testAttributeArgs, ...otherProps } = this.props;
    const classes = withStyles.getClasses(this.props);
    const hasError = Boolean(error || field?.error);

    return (
      <div className={classes.root}>
        <MuiSwitch
          {...getTestAttr(testAttributeArgs.module, testAttributeArgs.elementDescription, 'switch')}
          {...getScrollAttr(field?.name || otherProps.name)}
          checked={field ? field.value : checked}
          onChange={this.handleChange}
          {...otherProps}
        />
        {hasError && <Ellipsis classes={{ root: classes.errorText }} text={field?.error || errorText} />}
      </div>
    );
  }
}

const SwitchStyled = withStyles(
  Switch, 
  (theme, props) => ({
    "root": {
      position: 'relative'
    },
    "errorText": {
      ...theme.shortcuts.errorText,
      top: 22
    }
  })
);

export default SwitchStyled;